import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

/**
 * ContactForm — formulario de contacto reutilizable con EmailJS + reCAPTCHA.
 *
 * Props:
 *   onSuccess        {Function}  Callback ejecutado tras envío exitoso.
 *   className        {string}    Clases aplicadas al elemento <form>.
 *   showTypeSelector {boolean}   Muestra el selector Empresa / Startup / Partner.
 *
 * Comportamiento del modal:
 *   - Éxito: modal verde con mensaje de confirmación.
 *   - Error de envío: modal rojo con detalle del error.
 *   - Se cierra con el botón "×", el botón de acción, o clic en el backdrop.
 *   - Al cerrarse el modal de éxito, el formulario queda vacío y listo.
 */
export default function ContactForm({
  onSuccess,
  className = '',
  showTypeSelector = false,
}) {
  const { t } = useTranslation();
  const formRef = useRef();
  const recaptchaRef = useRef();

  const [form, setForm] = useState({
    full_name: '',
    company: '',
    corporate_email: '',
    phone: '',
    message: '',
    type: 'enterprise',
  });
  const [agreed, setAgreed] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  // modal: null | { type: 'success' | 'error' }
  const [modal, setModal] = useState(null);

  const closeModal = () => setModal(null);

  const validate = () => {
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = t('contact.errors.required');
    if (!form.corporate_email.trim()) errs.corporate_email = t('contact.errors.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.corporate_email))
      errs.corporate_email = t('contact.errors.invalidEmail');
    if (!form.message.trim()) errs.message = t('contact.errors.required');
    if (!agreed) errs.agreed = t('contact.errors.mustAccept');
    if (!recaptchaToken) errs.recaptcha = t('contact.errors.recaptchaRequired');
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSending(true);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      // Resetear formulario
      setForm({ full_name: '', company: '', corporate_email: '', phone: '', message: '', type: 'enterprise' });
      setAgreed(false);
      setErrors({});
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
      setModal({ type: 'success' });
      onSuccess?.();
    } catch (err) {
      console.error('EmailJS error:', err);
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      setModal({ type: 'error' });
    } finally {
      setSending(false);
    }
  };

  /* ─── Modal config por tipo ─── */
  const modalConfig = {
    success: {
      borderColor: 'border-green-500/30',
      iconBg: 'bg-green-500/10 border-green-500/30',
      iconColor: 'text-green-400',
      icon: 'check_circle',
      title: t('contact.form.successTitle'),
      message: t('contact.form.successMessage'),
      btnClass: 'bg-gradient-to-r from-purple-primary to-accent-magenta shadow-purple-primary/20',
      btnLabel: t('contact.form.closeModal'),
    },
    error: {
      borderColor: 'border-red-500/30',
      iconBg: 'bg-red-500/10 border-red-500/30',
      iconColor: 'text-red-400',
      icon: 'error',
      title: t('contact.errors.modalErrorTitle'),
      message: t('contact.errors.sendError'),
      supportNote: t('contact.errors.supportNote'),
      supportEmail: 'soporte@crosspaysolutions.com',
      btnClass: 'bg-red-500/80 hover:bg-red-500',
      btnLabel: t('contact.form.closeModal'),
    },
  };

  return (
    <>
      {/* ─── Modal (éxito / error) ─── */}
      <AnimatePresence>
        {modal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {/* Backdrop oscuro — clic cierra el modal */}
            <motion.div
              className="absolute inset-0 bg-black/75 backdrop-blur-sm"
              onClick={closeModal}
            />

            {/* Card del modal */}
            <motion.div
              className={`relative z-10 w-full max-w-md bg-[#1a1a1a] border ${modalConfig[modal.type].borderColor} rounded-2xl p-8 flex flex-col items-center text-center shadow-2xl`}
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Botón × cerrar */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 text-on-surface-variant/50 hover:text-white transition-colors"
                aria-label="Cerrar"
              >
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>

              {/* Ícono */}
              <div className={`w-16 h-16 rounded-full border flex items-center justify-center mb-5 ${modalConfig[modal.type].iconBg}`}>
                <span className={`material-symbols-outlined text-[36px] ${modalConfig[modal.type].iconColor}`}>
                  {modalConfig[modal.type].icon}
                </span>
              </div>

              <h3 className="text-2xl font-bold font-headline text-white mb-2">
                {modalConfig[modal.type].title}
              </h3>
              <p className="text-on-surface-variant text-sm leading-relaxed mb-4">
                {modalConfig[modal.type].message}
              </p>

              {/* Nota de soporte (solo en modal de error) */}
              {modal.type === 'error' && (
                <p className="text-on-surface-variant/60 text-xs leading-relaxed mb-6">
                  {modalConfig.error.supportNote}{' '}
                  <a
                    href={`mailto:${modalConfig.error.supportEmail}`}
                    className="text-accent-magenta hover:underline font-medium"
                  >
                    {modalConfig.error.supportEmail}
                  </a>
                </p>
              )}

              {modal.type !== 'error' && <div className="mb-6" />}

              <button
                onClick={closeModal}
                className={`px-6 py-2.5 text-white rounded-xl font-bold font-headline text-sm shadow-lg transition-opacity hover:opacity-90 ${modalConfig[modal.type].btnClass}`}
              >
                {modalConfig[modal.type].btnLabel}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Formulario ─── */}
      <form ref={formRef} onSubmit={handleSubmit} noValidate className={`space-y-4 ${className}`}>
        {/* Campos ocultos para EmailJS */}
        <input type="hidden" name="time" value={new Date().toLocaleString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'full', timeStyle: 'short' })} readOnly />
        <input type="hidden" name="company" value={form.company || '—'} readOnly />
        <input type="hidden" name="phone" value={form.phone || '—'} readOnly />

        {/* Selector de tipo (opcional) */}
        {showTypeSelector && (
          <div className="flex flex-wrap gap-3">
            {[
              { value: 'enterprise', label: t('contactPage.form.types.enterprise') },
              { value: 'startup', label: t('contactPage.form.types.startup') },
              { value: 'partner', label: t('contactPage.form.types.partner') },
            ].map((tp) => (
              <button
                key={tp.value}
                type="button"
                onClick={() => setForm({ ...form, type: tp.value })}
                className={`px-4 py-2 rounded-lg text-sm font-label font-medium transition-all duration-300 ${
                  form.type === tp.value
                    ? 'bg-purple-primary/20 border border-purple-primary text-accent-magenta'
                    : 'bg-transparent border border-outline-variant/30 text-on-surface-variant/60 hover:border-purple-primary/40'
                }`}
              >
                {tp.label}
              </button>
            ))}
          </div>
        )}

        {/* Nombre completo + Empresa */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cf-fullname" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
              {t('contact.form.fullName')}
            </label>
            <input
              id="cf-fullname" name="full_name" type="text"
              placeholder={t('contact.form.fullNamePlaceholder')}
              className={`w-full px-4 py-3 bg-[#111111] border rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all ${errors.full_name ? 'border-red-500' : 'border-outline-variant/30'}`}
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
            {errors.full_name && <p className="text-red-400 text-xs mt-1">{errors.full_name}</p>}
          </div>
          <div>
            <label htmlFor="cf-company" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
              {t('contact.form.company')}
            </label>
            <input
              id="cf-company" type="text"
              placeholder={t('contact.form.companyPlaceholder')}
              className="w-full px-4 py-3 bg-[#111111] border border-outline-variant/30 rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
            />
          </div>
        </div>

        {/* Correo corporativo + Teléfono */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="cf-email" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
              {t('contact.form.corporateEmail')}
            </label>
            <input
              id="cf-email" name="corporate_email" type="email"
              placeholder={t('contact.form.corporateEmailPlaceholder')}
              className={`w-full px-4 py-3 bg-[#111111] border rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all ${errors.corporate_email ? 'border-red-500' : 'border-outline-variant/30'}`}
              value={form.corporate_email}
              onChange={(e) => setForm({ ...form, corporate_email: e.target.value })}
            />
            {errors.corporate_email && <p className="text-red-400 text-xs mt-1">{errors.corporate_email}</p>}
          </div>
          <div>
            <label htmlFor="cf-phone" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
              {t('contact.form.phone')}
            </label>
            <input
              id="cf-phone" type="tel"
              placeholder={t('contact.form.phonePlaceholder')}
              className="w-full px-4 py-3 bg-[#111111] border border-outline-variant/30 rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
        </div>

        {/* Mensaje */}
        <div>
          <label htmlFor="cf-msg" className="block text-xs font-medium text-on-surface-variant/70 mb-1.5 uppercase tracking-wider">
            {t('contact.form.message')}
          </label>
          <textarea
            id="cf-msg" name="message" rows="4"
            placeholder={t('contact.form.messagePlaceholder')}
            className={`w-full px-4 py-3 bg-[#111111] border rounded-lg text-white text-sm placeholder-gray-600 focus:border-purple-primary focus:ring-1 focus:ring-purple-primary/50 transition-all resize-vertical ${errors.message ? 'border-red-500' : 'border-outline-variant/30'}`}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
        </div>

        {/* Términos */}
        <div className="flex items-start gap-3">
          <input
            id="cf-terms" type="checkbox"
            className="mt-1 w-4 h-4 rounded border-purple-primary/50 bg-[#111111] accent-[#A84397]"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <label htmlFor="cf-terms" className="text-xs text-on-surface-variant/70">
            {t('contact.form.terms')}{' '}
            <Link to="/terms" className="text-accent-magenta hover:underline">{t('contact.form.termsLink')}</Link>{' '}
            {t('contact.form.termsConnector')}{' '}
            <Link to="/privacy" className="text-accent-magenta hover:underline">{t('contact.form.privacyLink')}</Link>
          </label>
        </div>
        {errors.agreed && <p className="text-red-400 text-xs">{errors.agreed}</p>}

        {/* reCAPTCHA */}
        <div className="flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={setRecaptchaToken}
            onExpired={() => setRecaptchaToken(null)}
            theme="dark"
          />
        </div>
        {errors.recaptcha && <p className="text-red-400 text-xs text-center">{errors.recaptcha}</p>}

        {/* Botón de envío */}
        <motion.button
          type="submit"
          disabled={sending}
          className="w-full py-4 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline shadow-lg shadow-purple-primary/20 relative overflow-hidden group disabled:opacity-60"
          whileHover={{ scale: sending ? 1 : 1.02, boxShadow: sending ? 'none' : '0 0 30px rgba(168,67,151,0.3)' }}
          whileTap={{ scale: sending ? 1 : 0.98 }}
        >
          <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <span className="relative flex items-center justify-center gap-2">
            {sending ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t('contact.form.sending')}
              </>
            ) : (
              <>
                {t('contact.form.submit')}
                <span className="material-symbols-outlined text-[18px]">send</span>
              </>
            )}
          </span>
        </motion.button>
      </form>
    </>
  );
}
